/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Backdrop from "@mui/material/Backdrop";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import { useTranslation } from "react-i18next";
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import { useSelector } from "react-redux";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  code: "",
  supplier_name: "",
  type_name: "",
  date_record: "",
  remark: "",
  user_name: "",
  status: "purchase",
};

const stepperName = {
  edit: {
    name: "แก้ไขข้อมูล",
    color: "warning",
    icon: "border_color",
  },
  purchase: {
    name: "สั่งซื้อ",
    color: "info",
    icon: "task_alt",
  },
  receive: {
    name: "รับบางส่วน",
    color: "info",
    icon: "task_alt",
  },
  success: {
    name: "ได้รับครบ",
    color: "success",
    icon: "task_alt",
  },
  reject: {
    name: "ยกเลิก",
    color: "error",
    icon: "cancel",
  },
};

function CreditorOpen({ parent }) {
  const mod = "creditor_po";
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const open = () => {
    setLoading(true);
    setAttr(initAttr);
    setList([]);

    FetchData("GET", `/${mod}/${parent.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === "ok") {
        setAttr(result.data);
        setList(result.list);

        setLoading(false);
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant="rounded" />;
  };

  const statusDiaplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: "14px",
          padding: "0 10px",
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: "nowrap",
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    return (
      <Paper variant="outlined" sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant="h5" mb={3}>
          #{attr.code}
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label="ผู้จัดจำหน่าย"
              value={attr.supplier_name}
              variant="standard"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="ประเภทสั่งซื้อ"
              value={attr.type_name}
              variant="standard"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="ผู้สั่งซื้อ"
              value={attr.user_name}
              variant="standard"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="วันสั่งซื้อ"
              value={Func.dateDisplay(attr.date_record)}
              variant="standard"
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label="หมายเหตุสั่งซื้อ"
              value={attr.remark}
              variant="standard"
              multiline
              InputProps={{
                readOnly: true,
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth="xxl"
        keepMounted
        aria-describedby="dialog-add"
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          {attr.code}
          <IconButton
            aria-label="close"
            onClick={() => setModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={0}>
            <Grid container mb={3} spacing={2}>
              <Grid item xs={12}>
                {statusDiaplay(attr)}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={3}>
                {renderInfo()}
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={9}>
                <MDBox sx={{ border: "1px solid #e2e2e2", borderRadius: 1 }}>
                  <MDBox p={2}>
                    <MDTypography variant="h5">{t(`element.order`)}</MDTypography>
                  </MDBox>
                  <TableContainer sx={{ boxShadow: 0 }}>
                    <Table aria-label="simple table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">No.</TableCell>
                          <TableCell align="center">รูป</TableCell>
                          <TableCell>สินค้า</TableCell>
                          <TableCell align="right">จำนวน</TableCell>
                          <TableCell align="right">รับแล้ว</TableCell>
                          <TableCell align="right">รอรับ</TableCell>
                          <TableCell>หน่วย</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {list.map((row, i) => (
                          <TableRow key={row.id}>
                            <TableCell align="center" width="100">
                              {i + 1}
                            </TableCell>
                            <TableCell width="40">{imageDisplay(row)}</TableCell>
                            <TableCell align="left">{row.product_name}</TableCell>
                            <TableCell align="right">{Func.numberDisplay(row.quantity)}</TableCell>
                            <TableCell align="right">
                              {Func.numberDisplay(row.quantity_receive)}
                            </TableCell>
                            <TableCell align="right">
                              {Func.numberDisplay(row.quantity_balance)}
                            </TableCell>
                            <TableCell align="left">{row.unit_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
  }, []);

  return (
    <div>
      <MDButton
        variant="text"
        color="info"
        size="small"
        sx={{ fontSize: "1rem", whiteSpace: "nowrap" }}
        onClick={() => {
          open();
          setModalOpen(true);
        }}
      >
        {parent.code}
      </MDButton>
      {renderOpen()}
      {renderLoading()}
    </div>
  );
}

CreditorOpen.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
  }).isRequired,
};

export default CreditorOpen;
